import { DEAL_CONVERTIBLE_STATUSES, TODAY } from '../../constants';
import { activateClient, fetchClientJiraKey } from '../../services/clients';
import { insertDealStatusChange } from '../../services/dealActivities';
import { insertPhaseFromDeal } from '../../services/phases';
import { notify } from '../../store/feedback';
import { dealLineIncomplete } from '../../utils/budget';
import { isoStr } from '../../utils/dates';
import { uid } from '../../utils/format';
import { draftJiraKey } from '../../utils/jira';
import { dealRow, statusDatesPatch } from './dealDefaults';
import { lineToPhaseDraft } from './phaseDraft';
import { linesForConversion } from './phaseDraftLine';

/* Conversion d'une affaire gagnée en projet : une phase projet par ligne vendue (hébergement
   exclu), puis import des epics Jira des phases au forfait par brique. */
export default function dealConversionHandlers(deps, api) {
  const { dealModal, convertModal, loadAppData, saveDealLines, session } = deps;
  const { setDealModal, setConvertModal, setEpicImportQueue } = deps;

  // Ouvre la modal de mapping avant conversion en projet delivery
  async function openConvertModal() {
    const m = dealModal;
    if (!m?.id) return;
    if (!m.client_id) {
      notify("Associe d'abord un client à l'affaire pour la convertir.");
      return;
    }
    if (m.converted_client_id) {
      notify('Cette affaire est déjà convertie en projet.');
      return;
    }
    if (!DEAL_CONVERTIBLE_STATUSES.includes(m.statut))
      return void notify(
        'Seule une affaire gagnée (Devis accepté ou En cours) se convertit en projet.',
      );
    // Même contrôle des lignes vendues que le passage en statut gagné : un projet a toujours un
    // vendu signé.
    const problemes = (m.lignes || []).map(dealLineIncomplete).filter(Boolean);
    if (problemes.length) return void setDealModal((o) => ({ ...o, _gateError: problemes }));
    // Sauvegarde silencieuse de l'affaire et de ses lignes avant d'ouvrir la conversion
    // (les modifications en cours sont ainsi persistées sans fermer la modale)
    const lignes = m.lignes && m.lignes.length ? m.lignes : [api.blankDealLine()];
    if ((await api.saveDealPatch(m.id, dealRow({ ...m, nom: m.nom || '' }, m.client_id)))?.error)
      return;
    const linesError = await saveDealLines(m.id, lignes);
    if (linesError)
      return void notify(`Lignes de l'affaire non enregistrées : ${linesError.message}`);
    const phases = lignes.filter((l) => l.facturation !== 'hebergement').map(lineToPhaseDraft);
    const skipped = lignes.filter((l) => l.facturation === 'hebergement');
    // Pré-remplit avec la clé déjà connue du client (ex. converti une 1re fois, ou déjà utilisée sur
    // une autre phase) : sinon la RH doit la retaper à chaque conversion alors qu'elle est censée
    // s'appliquer par défaut à toutes les phases qui ne la redéfinissent pas.
    const { data: clientRow } = await fetchClientJiraKey(m);
    setConvertModal({
      affaire: { ...m, lignes },
      phases,
      skipped,
      jiraKeyGlobal: clientRow?.jira_project_key || '',
    });
  }

  // Exécute la conversion après validation du mapping (une phase projet par ligne, hébergement exclu)
  async function confirmConvert() {
    const { affaire: m, jiraKeyGlobal } = convertModal;
    // Chaque phase a sa ligne vendue : dates reportées sur la ligne d'origine, ligne créée pour
    // une phase ajoutée à la main ; toutes les lignes sont contrôlées avant de convertir.
    const { lines, phases } = linesForConversion(m.lignes, convertModal.phases);
    const problemes = lines.map(dealLineIncomplete).filter(Boolean);
    if (problemes.length) return void notify(`Conversion impossible :\n${problemes.join('\n')}`);
    await saveDealLines(m.id, lines);
    const globalKey = (jiraKeyGlobal || '').trim().toUpperCase();
    // La clé globale devient la clé par défaut du client (pas juste de cette conversion) : toute
    // phase future sans clé propre l'héritera automatiquement, pas seulement celles créées ici.
    await activateClient(globalKey, m);
    let firstPhaseId = null;
    const epicQueue = [];
    for (const ph of phases) {
      const phId = 'ph_' + uid();
      const fact = ph.facturation || 'forfait_brique';
      const jiraKey = draftJiraKey(ph.jira_project_key, globalKey);
      // Une phase non créée ne doit jamais passer en silence (cas d'une régie sans montant, refusée
      // par la base avant le 2026-10-02) : on s'arrête et on le dit.
      const { error } = await insertPhaseFromDeal(fact, jiraKey, m, ph, phId);
      if (error) return void notify(`Phase « ${ph.nom} » non créée : ${error.message}`);
      if (!firstPhaseId) firstPhaseId = phId;
      if (fact === 'forfait_brique' && jiraKey)
        epicQueue.push({ clientId: m.client_id, phaseId: phId, jiraKey });
    }
    await api.saveDealPatch(m.id, {
      statut: 'en_cours',
      converted_client_id: m.client_id,
      converted_phase_id: firstPhaseId,
      ...statusDatesPatch(m, 'en_cours'),
    });
    // Le passage en « En cours » par la conversion est tracé dans l'historique, comme un
    // changement de statut manuel.
    if (m.statut !== 'en_cours')
      await insertDealStatusChange(m.id, 'en_cours', session, isoStr(TODAY));
    setConvertModal(null);
    setDealModal(null);
    await api.loadDeals();
    await loadAppData();
    if (epicQueue.length) {
      const [first, ...rest] = epicQueue;
      setEpicImportQueue(rest);
      await api.openEpicImportForQueueItem(first);
    }
  }

  return { openConvertModal, lineToPhaseDraft, confirmConvert };
}
